import { useState, useTransition } from "react";
import { toast } from "sonner";
import { Icone } from "@/components/Icone";
import { Ajustes, type SecaoEmpresa } from "@/components/casca/Ajustes";
import { Area, Aviso, Botao, Campo, Chave, Escolha, Secao } from "@/components/casca/formulario";
import { AVISOS_PADRAO, DIAS, NOME_DO_DIA, type Avisos, type DiaSemana, type Expediente, type Unidade } from "@/lib/tenants/types";
import type { Cadastro } from "@/lib/telas/empresa";
import type { Tenant } from "@/lib/tenants/types";
import { salvarEmpresaAction } from "@/web/src/acoes-config";

const FUSOS = [
  "America/Sao_Paulo", "America/Manaus", "America/Belem", "America/Fortaleza", "America/Recife",
  "America/Bahia", "America/Cuiaba", "America/Campo_Grande", "America/Porto_Velho", "America/Rio_Branco",
];

const HORA =
  "w-full rounded-lg border border-[var(--color-borda)] bg-[var(--color-cartao)] px-2 py-1 text-[13px] text-[var(--color-tinta)] outline-none focus:border-[var(--color-marca)]";

/** Um id estável a partir do nome — ou numerado, quando o nome não ajuda. */
function novoId(prefixo: string, nome: string, ocupados: string[]): string {
  const base =
    nome
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 24) || `${prefixo}_${ocupados.length + 1}`;
  let id = base;
  for (let n = 2; ocupados.includes(id); n++) id = `${base}-${n}`;
  return id;
}

const expedienteNovo = (): Expediente[] =>
  DIAS.map((dia) => ({
    dia,
    abre: dia === "dom" || dia === "sab" ? null : "09:00",
    fecha: dia === "dom" || dia === "sab" ? null : "18:00",
  }));

const troca = <T,>(lista: T[], i: number, parte: Partial<T>): T[] => lista.map((x, j) => (j === i ? { ...x, ...parte } : x));

/**
 * O cadastro inteiro, mostrado em seis páginas curtas (Ajustes › A clínica) e
 * salvo de uma vez — porque as regras cruzam seções: um serviço aponta para
 * profissionais e unidades, e a validação (no servidor) só faz sentido sobre o
 * conjunto.
 *
 * O que está aqui é o que o assistente sabe.
 */
export function FormEmpresa({
  secao,
  inicial,
  agenteInicial,
  constituicaoInicial,
}: {
  secao: SecaoEmpresa;
  inicial: Cadastro;
  agenteInicial: Tenant["agente"];
  constituicaoInicial: string;
}) {
  const [t, setT] = useState<Cadastro>(inicial);
  const [agente, setAgente] = useState(agenteInicial);
  const [constituicao, setConstituicao] = useState(constituicaoInicial);
  const [problemas, setProblemas] = useState<string[]>([]);
  const [salvo, setSalvo] = useState(false);
  const [pendente, iniciar] = useTransition();
  // O formulário vale para as seis páginas: trocar de página não perde o que
  // foi digitado, e "Salvar" grava tudo junto. `mexeu` avisa que há algo por salvar.
  const [mexeu, setMexeu] = useState(false);
  const marcarMexeu = () => {
    setSalvo(false);
    setMexeu(true);
  };
  const mudar = (parte: Partial<Cadastro>) => {
    setT((a) => ({ ...a, ...parte }));
    marcarMexeu();
  };

  function salvar() {
    setProblemas([]);
    iniciar(async () => {
      const r = await salvarEmpresaAction({ cadastro: t, agente, constituicao });
      if (!r.ok) {
        setProblemas(r.problemas ?? [r.erro]);
        toast.error(r.erro);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      setSalvo(true);
      setMexeu(false);
      toast.success("Salvo. O assistente já usa o novo.");
    });
  }

  const avisos = t.avisos ?? AVISOS_PADRAO;
  const mudarAvisos = (parte: Partial<Avisos>) => mudar({ avisos: { ...avisos, ...parte } });

  const idsUnidades = t.unidades.map((u) => u.id);
  const idsProfs = t.profissionais.map((p) => p.id);
  const varias = t.unidades.length > 1;

  const TITULOS: Record<SecaoEmpresa, { titulo: string; descricao: string }> = {
    clinica: { titulo: "Clínica e assistente", descricao: "O que está aqui é o que o assistente sabe sobre a clínica. Mudou e salvou, ele já responde com o novo." },
    unidades: { titulo: "Unidades e horários", descricao: "Onde a clínica atende e quando. O assistente só oferece horário dentro do expediente." },
    profissionais: { titulo: "Profissionais", descricao: "Quem atende, em que dias e em quais unidades." },
    servicos: { titulo: "Serviços", descricao: "O que a clínica faz, quanto custa e quanto tempo leva. O assistente informa exatamente o que estiver aqui." },
    avisos: { titulo: "Avisos automáticos", descricao: "O que o sistema manda sozinho, pelo WhatsApp da clínica. Tudo começa desligado." },
    constituicao: { titulo: "Constituição", descricao: "Tudo que o assistente sabe além do cadastro: políticas, promoções, perguntas frequentes. Se não está aqui, ele não sabe." },
  };
  const { titulo, descricao } = TITULOS[secao];

  return (
    <Ajustes
      titulo={titulo}
      descricao={descricao}
      acao={
        <>
          {mexeu && !salvo && !pendente && <span className="text-[13px] font-semibold text-[var(--color-atencao)]">Alterações não salvas</span>}
          <Botao onClick={salvar} disabled={pendente} icone={salvo ? "check" : undefined}>
            {pendente ? "Salvando…" : salvo ? "Salvo" : "Salvar"}
          </Botao>
        </>
      }
    >
      {problemas.length > 0 && (
        <Aviso tom="alerta" titulo="Não salvei — isto quebraria o assistente:">
          <ul className="list-disc space-y-0.5 pl-4">
            {problemas.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </Aviso>
      )}

      {secao === "clinica" && (
        <>
          <Secao titulo="Identificação" ajuda="Como a clínica aparece para o paciente.">
            <div className="grid gap-x-4 sm:grid-cols-2">
              <Campo rotulo="Nome" value={t.nome} onChange={(e) => mudar({ nome: e.target.value })} />
              <Campo rotulo="Telefone" value={t.contato.telefone ?? ""} onChange={(e) => mudar({ contato: { ...t.contato, telefone: e.target.value } })} />
              <Campo rotulo="Endereço" value={t.contato.endereco ?? ""} onChange={(e) => mudar({ contato: { ...t.contato, endereco: e.target.value } })} />
              <Escolha rotulo="Fuso horário" value={t.timezone} onChange={(e) => mudar({ timezone: e.target.value })}>
                {FUSOS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </Escolha>
            </div>
          </Secao>
          <Secao titulo="O assistente" ajuda="Como ele se apresenta, o que tenta conseguir na conversa e como fala.">
            <Campo rotulo="Como se apresenta" value={agente.nome} onChange={(e) => { setAgente({ ...agente, nome: e.target.value }); marcarMexeu(); }} />
            <Area rotulo="Objetivo" rows={2} value={agente.objetivo} onChange={(e) => { setAgente({ ...agente, objetivo: e.target.value }); marcarMexeu(); }} />
            <Area rotulo="Tom de voz" rows={2} value={agente.tom} onChange={(e) => { setAgente({ ...agente, tom: e.target.value }); marcarMexeu(); }} />
          </Secao>
        </>
      )}

      {secao === "unidades" && (
        <>
          {t.unidades.map((u, iu) => (
            <Secao
              key={u.id}
              titulo={u.nome || "Unidade sem nome"}
              ajuda={t.unidades.length > 1 ? "Com mais de uma unidade, o assistente pergunta em qual a pessoa quer ser atendida antes de oferecer horário." : undefined}
              acao={
                <button
                  type="button"
                  aria-label={`Remover a unidade ${u.nome || ""}`}
                  disabled={t.unidades.length === 1}
                  onClick={() => mudar({ unidades: t.unidades.filter((_, j) => j !== iu) })}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--color-fraco)] transition-colors hover:bg-[var(--color-alerta-fundo)] hover:text-[var(--color-alerta)] disabled:opacity-30"
                >
                  <Icone nome="lixeira" className="h-4 w-4" />
                </button>
              }
            >
              <div className="grid gap-x-4 sm:grid-cols-2">
                <Campo rotulo="Nome da unidade" placeholder="Maringá, Centro…" value={u.nome} onChange={(e) => mudar({ unidades: troca(t.unidades, iu, { nome: e.target.value }) })} />
                <Campo rotulo="Telefone desta unidade" value={u.telefone ?? ""} onChange={(e) => mudar({ unidades: troca(t.unidades, iu, { telefone: e.target.value }) })} />
              </div>
              <Campo rotulo="Endereço completo" value={u.endereco ?? ""} onChange={(e) => mudar({ unidades: troca(t.unidades, iu, { endereco: e.target.value }) })} />

              <p className="mt-1 mb-2 text-[13px] font-semibold text-[var(--color-suave)]">Expediente</p>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(min(116px,100%),1fr))] gap-2">
                {u.expediente.map((e, ie) => {
                  const aberto = Boolean(e.abre && e.fecha);
                  const trocaExp = (parte: Partial<Expediente>) =>
                    mudar({ unidades: troca(t.unidades, iu, { expediente: troca(u.expediente, ie, parte) }) });
                  const dia = NOME_DO_DIA[e.dia].replace("-feira", "");
                  return (
                    <div key={e.dia} className={"flex flex-col gap-1.5 rounded-xl p-2.5 " + (aberto ? "bg-[var(--color-painel)]" : "bg-[var(--color-painel)] opacity-70")}>
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-[13px] font-bold capitalize">{dia}</span>
                        <Chave
                          rotulo={`Aberto ${dia}`}
                          ligado={aberto}
                          onChange={(v) => trocaExp(v ? { abre: "09:00", fecha: "18:00" } : { abre: null, fecha: null })}
                        />
                      </div>
                      {aberto ? (
                        <div className="flex flex-col gap-1">
                          <input type="time" aria-label={`Abre ${dia}`} value={e.abre ?? ""} onChange={(ev) => trocaExp({ abre: ev.target.value })} className={HORA} />
                          <input type="time" aria-label={`Fecha ${dia}`} value={e.fecha ?? ""} onChange={(ev) => trocaExp({ fecha: ev.target.value })} className={HORA} />
                        </div>
                      ) : (
                        <span className="text-[12.5px] text-[var(--color-fraco)]">fechado</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </Secao>
          ))}
          <div>
            <Botao
              variante="secundario"
              icone="mais"
              onClick={() => mudar({ unidades: [...t.unidades, { id: novoId("u", `unidade ${t.unidades.length + 1}`, idsUnidades), nome: "", expediente: expedienteNovo() } as Unidade] })}
            >
              Unidade
            </Botao>
          </div>
          <Secao titulo="Como a agenda oferece horário" ajuda="Vale para todas as unidades.">
            <div className="grid gap-x-4 sm:grid-cols-3">
              <Campo rotulo="Passo da grade (min)" type="number" min={5} step={5} value={t.agenda.passoMin} onChange={(e) => mudar({ agenda: { ...t.agenda, passoMin: Number(e.target.value) } })} />
              <Campo rotulo="Antecedência mínima (h)" type="number" min={0} value={t.agenda.antecedenciaMinHoras} onChange={(e) => mudar({ agenda: { ...t.agenda, antecedenciaMinHoras: Number(e.target.value) } })} />
              <Campo rotulo="Oferece até (dias à frente)" type="number" min={1} max={90} value={t.agenda.janelaDias} onChange={(e) => mudar({ agenda: { ...t.agenda, janelaDias: Number(e.target.value) } })} />
            </div>
          </Secao>
        </>
      )}

      {secao === "profissionais" && (
        <>
          {t.profissionais.length === 0 && <p className="text-sm text-[var(--color-suave)]">Nenhum profissional cadastrado.</p>}
          {t.profissionais.map((p, ip) => (
            <Secao
              key={p.id}
              titulo={p.nome || "Profissional sem nome"}
              ajuda="Os dias marcados são os únicos em que o assistente oferece horário com esta pessoa."
              acao={
                <button
                  type="button"
                  aria-label={`Remover ${p.nome || "profissional"}`}
                  onClick={() =>
                    mudar({
                      profissionais: t.profissionais.filter((_, j) => j !== ip),
                      servicos: t.servicos.map((s) => ({ ...s, profissionais: s.profissionais.filter((x) => x !== p.id) })),
                    })
                  }
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--color-fraco)] transition-colors hover:bg-[var(--color-alerta-fundo)] hover:text-[var(--color-alerta)]"
                >
                  <Icone nome="lixeira" className="h-4 w-4" />
                </button>
              }
            >
              <div className="grid gap-x-4 sm:grid-cols-2">
                <Campo rotulo="Nome" value={p.nome} onChange={(e) => mudar({ profissionais: troca(t.profissionais, ip, { nome: e.target.value }) })} />
                <Campo rotulo="Especialidade" value={p.especialidade} onChange={(e) => mudar({ profissionais: troca(t.profissionais, ip, { especialidade: e.target.value }) })} />
              </div>
              <Marcadores rotulo="Atende nos dias" opcoes={DIAS.map((d) => ({ id: d, rotulo: NOME_DO_DIA[d].replace("-feira", "") }))} marcados={p.dias} onChange={(dias) => mudar({ profissionais: troca(t.profissionais, ip, { dias: dias as DiaSemana[] }) })} />
              {varias && (
                <Marcadores rotulo="Nas unidades (nenhuma = todas)" opcoes={t.unidades.map((u) => ({ id: u.id, rotulo: u.nome || u.id }))} marcados={p.unidades} onChange={(unidades) => mudar({ profissionais: troca(t.profissionais, ip, { unidades }) })} />
              )}
            </Secao>
          ))}
          <div>
            <Botao variante="secundario" icone="mais" onClick={() => mudar({ profissionais: [...t.profissionais, { id: novoId("p", `profissional ${t.profissionais.length + 1}`, idsProfs), nome: "", especialidade: "", dias: ["seg", "ter", "qua", "qui", "sex"], unidades: [] }] })}>
              Profissional
            </Botao>
          </div>
        </>
      )}

      {secao === "servicos" && (
        <>
          {t.servicos.length === 0 && <p className="text-sm text-[var(--color-suave)]">Nenhum serviço cadastrado.</p>}
          {t.servicos.map((s, is) => (
            <Secao
              key={s.id}
              titulo={s.nome || "Serviço sem nome"}
              acao={
                <button
                  type="button"
                  aria-label={`Remover ${s.nome || "serviço"}`}
                  onClick={() => mudar({ servicos: t.servicos.filter((_, j) => j !== is) })}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--color-fraco)] transition-colors hover:bg-[var(--color-alerta-fundo)] hover:text-[var(--color-alerta)]"
                >
                  <Icone nome="lixeira" className="h-4 w-4" />
                </button>
              }
            >
              <Campo rotulo="Nome do serviço" value={s.nome} onChange={(e) => mudar({ servicos: troca(t.servicos, is, { nome: e.target.value }) })} />
              <Campo rotulo="O que é, em uma linha" placeholder="Vai para a constituição" value={s.descricao} onChange={(e) => mudar({ servicos: troca(t.servicos, is, { descricao: e.target.value }) })} />
              <div className="grid gap-x-4 sm:grid-cols-[1fr_160px]">
                <Campo rotulo="Preço" placeholder="R$ 150, a partir de R$ 600, gratuita…" ajuda="Sai exatamente como escrito aqui." value={s.preco} onChange={(e) => mudar({ servicos: troca(t.servicos, is, { preco: e.target.value }) })} />
                <Campo rotulo="Duração (min)" type="number" min={5} step={5} ajuda="Decide se o horário cabe." value={s.duracaoMin} onChange={(e) => mudar({ servicos: troca(t.servicos, is, { duracaoMin: Number(e.target.value) }) })} />
              </div>
              <Marcadores rotulo="Quem faz (nenhum = qualquer um)" opcoes={t.profissionais.map((p) => ({ id: p.id, rotulo: p.nome || p.id }))} marcados={s.profissionais} onChange={(profissionais) => mudar({ servicos: troca(t.servicos, is, { profissionais }) })} />
              {varias && (
                <Marcadores rotulo="Onde (nenhuma = todas)" opcoes={t.unidades.map((u) => ({ id: u.id, rotulo: u.nome || u.id }))} marcados={s.unidades} onChange={(unidades) => mudar({ servicos: troca(t.servicos, is, { unidades }) })} />
              )}
            </Secao>
          ))}
          <div>
            <Botao variante="secundario" icone="mais" onClick={() => mudar({ servicos: [...t.servicos, { id: novoId("s", `servico ${t.servicos.length + 1}`, t.servicos.map((x) => x.id)), nome: "", descricao: "", preco: "", duracaoMin: 60, profissionais: [], unidades: [] }] })}>
              Serviço
            </Botao>
          </div>
        </>
      )}

      {secao === "avisos" && (
        <>
          <Secao titulo="Aviso à equipe pelo WhatsApp" ajuda="Quando o assistente passar uma conversa para a equipe, este número recebe o motivo e o link da conversa. Vazio: o aviso fica só no painel.">
            <Campo
              rotulo="WhatsApp da equipe"
              placeholder="(44) 99999-9999"
              value={avisos.equipeWhatsApp}
              onChange={(e) => mudarAvisos({ equipeWhatsApp: e.target.value })}
              ajuda="O assistente não responde mensagens vindas deste número — não use o celular com que você testa como paciente."
            />
          </Secao>
          <Secao
            titulo="Lembrar o paciente antes da consulta"
            ajuda="Uma mensagem só, para o telefone de cada agendamento: serviço, dia, hora e com quem — e o convite a responder se precisar remarcar; o assistente continua a conversa. Quem falou com a clínica nas últimas 3 horas não recebe."
            acao={<Chave rotulo="Lembrar o paciente antes da consulta" ligado={avisos.lembrete.ativo} onChange={(v) => mudarAvisos({ lembrete: { ...avisos.lembrete, ativo: v } })} />}
          >
            <div className="max-w-[220px]">
              <Campo
                rotulo="Quantas horas antes"
                type="number"
                min={1}
                max={72}
                disabled={!avisos.lembrete.ativo}
                value={avisos.lembrete.horasAntes}
                onChange={(e) => mudarAvisos({ lembrete: { ...avisos.lembrete, horasAntes: Number(e.target.value) } })}
              />
            </div>
          </Secao>
        </>
      )}

      {secao === "constituicao" && (
        <section className="rounded-[14px] bg-[var(--color-cartao)] p-5 shadow-cartao">
          <textarea
            value={constituicao}
            aria-label="Constituição do assistente"
            onChange={(e) => {
              setConstituicao(e.target.value);
              marcarMexeu();
            }}
            spellCheck={false}
            className="min-h-[480px] w-full rounded-[10px] border border-[var(--color-borda)] bg-[var(--color-painel)] px-3.5 py-3 font-mono text-[13px] leading-relaxed text-[var(--color-tinta)] outline-none transition-colors focus:border-[var(--color-marca)] focus:bg-[var(--color-cartao)]"
          />
          <p className="mt-1.5 text-xs text-[var(--color-fraco)]">{constituicao.length.toLocaleString("pt-BR")} caracteres · Markdown</p>
        </section>
      )}
    </Ajustes>
  );
}

function Marcadores({
  rotulo,
  opcoes,
  marcados,
  onChange,
}: {
  rotulo: string;
  opcoes: Array<{ id: string; rotulo: string }>;
  marcados: string[];
  onChange: (ids: string[]) => void;
}) {
  return (
    <div className="mt-1 mb-3">
      <p className="mb-1.5 text-[13px] font-semibold text-[var(--color-suave)]">{rotulo}</p>
      <div className="flex flex-wrap gap-1.5">
        {opcoes.map((o) => {
          const marcado = marcados.includes(o.id);
          return (
            <label
              key={o.id}
              className={
                "flex h-8 cursor-pointer items-center gap-1.5 rounded-full px-3 text-[13px] font-semibold capitalize transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-dashed has-[:focus-visible]:outline-[var(--color-marca)] " +
                (marcado ? "bg-[var(--color-realce)] text-[var(--color-marca-forte)]" : "bg-[var(--color-painel)] text-[var(--color-suave)] hover:text-[var(--color-tinta)]")
              }
            >
              <input
                type="checkbox"
                checked={marcado}
                onChange={(e) => onChange(e.target.checked ? [...marcados, o.id] : marcados.filter((x) => x !== o.id))}
                className="sr-only"
              />
              {marcado && <Icone nome="check" className="h-3.5 w-3.5" />}
              {o.rotulo}
            </label>
          );
        })}
        {opcoes.length === 0 && <span className="text-[13px] text-[var(--color-fraco)]">nada cadastrado ainda</span>}
      </div>
    </div>
  );
}
